import { formatInTimeZone } from 'date-fns-tz';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';
import { MAX_PLANNING_RANGE_DAYS } from 'src/utils/planningDateRange.util';

const JP_WEEKDAY = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** 'YYYY-MM-DD' を Date（ローカル 0 時）にする。読めなければ null */
function parseDay(day: string): Date | null {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!matched) return null;
  const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * 業務日の基準。ブラウザのタイムゾーンで日付を出すと、日境界の勤務・案件が別日のタブへ
 * 分類される（UTC 環境では 2026-07-03T00:30:00+09:00 が 7/2 扱いになる。Codex レビュー指摘）。
 */
const BUSINESS_TIME_ZONE = 'Asia/Tokyo';

/** ISO → 'YYYY-MM-DD'（業務日 = JST）。date-fns の format は timeZone を無視するので使わない */
function toBusinessDayString(date: Date): string {
  return formatInTimeZone(date, BUSINESS_TIME_ZONE, 'yyyy-MM-dd');
}

/** Date → 'YYYY-MM-DD'（ローカル）。ISO 変換だと UTC へずれて前日になる */
function toDayString(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * 表示日タブに並べる日。開始日から終了日まで（両端を含む）。
 *
 * 逆転・読めない終了日は開始日 1 日だけにする（URL 直打ちで来ても画面を落とさない）。
 * 上限は一度に取得できる日数（MAX_PLANNING_RANGE_DAYS）に合わせる。
 */
export function buildDisplayDays(startOn: string, endOn: string): string[] {
  const start = parseDay(startOn);
  if (!start) return [];
  const end = parseDay(endOn);
  if (!end || end < start) return [startOn];

  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end && days.length < MAX_PLANNING_RANGE_DAYS) {
    days.push(toDayString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/** タブのラベル。'3/16(月)' */
export function formatDisplayDayLabel(day: string): string {
  const date = parseDay(day);
  if (!date) return day;
  return `${date.getMonth() + 1}/${date.getDate()}(${JP_WEEKDAY[date.getDay()]})`;
}

/**
 * いま選ぶべき日。期間を変えたあとに前の選択が残ると、どの行にも当たらず
 * 1 台も出ない画面になるので先頭の日へ戻す。
 */
export function resolveSelectedDay(days: readonly string[], current: string | null): string {
  if (current && days.includes(current)) return current;
  return days[0] ?? '';
}

/**
 * ISO 日時が属する表示日。期間の外にある日時は端の日へ寄せる。
 *
 * 勤務は期間と重なれば取得されるので、前日の夜から始まる勤務は開始日が期間より前に
 * なりうる。そのままだとどのタブにも出ず、画面から触れなくなる。
 */
export function resolveDayOf(
  iso: string | null | undefined,
  days: readonly string[],
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const day = toBusinessDayString(date);
  if (days.length === 0) return day;
  if (day < days[0]) return days[0];
  if (day > days[days.length - 1]) return days[days.length - 1];
  return day;
}

/** 勤務 (shift_truck) ごとの表示日。日は勤務の開始日で決める */
export function buildDayByShiftTruckId(
  deliveries: readonly { id: number; startAt?: string | null }[],
  days: readonly string[],
): Map<number, string> {
  const result = new Map<number, string>();
  deliveries.forEach((delivery) => {
    const day = resolveDayOf(delivery.startAt, days);
    if (day) result.set(delivery.id, day);
  });
  return result;
}

/**
 * 車両リストを表示日で絞る。
 *
 * 日が引けない行（勤務を持たない固定ルート由来の合成行など）は落とさない。
 * どのタブにも出ないと画面から触れなくなるため。
 */
export function filterRowGroupsByDay(
  groups: VehicleRowGroup[],
  day: string | null,
  dayByShiftTruckId: ReadonlyMap<number, string>,
): VehicleRowGroup[] {
  if (!day) return groups;
  return groups.filter((group) => {
    if (group.shiftTruckId == null) return true;
    const groupDay = dayByShiftTruckId.get(group.shiftTruckId);
    return groupDay == null || groupDay === day;
  });
}

/**
 * 未割当案件を表示日で絞る。日は積地の指定開始時刻で決める。
 *
 * API が積地の指定時刻（shipment_order_waypoint_times.start_at）で期間を絞っている
 * （ShipmentOrderLegRuns::Search）ので、こちらも同じ列で見る。降地の時刻で見ると、
 * 日付をまたぐ運行がどのタブにも出なくなる。
 */
export function filterLegRunsByDay<T extends { loading_start_at?: string | null }>(
  legRuns: T[],
  day: string | null,
  days: readonly string[],
): T[] {
  if (!day) return legRuns;
  return legRuns.filter((legRun) => {
    const legRunDay = resolveDayOf(legRun.loading_start_at, days);
    return legRunDay == null || legRunDay === day;
  });
}
